/**
 * © 2026 Madar.iq — جميع الحقوق محفوظة
 * هذا الملف جزء من منظومة Madar.iq البرمجية.
 * يُحظر نسخه أو تعديله أو توزيعه دون إذن كتابي.
 * All Rights Reserved — Unauthorized use is prohibited.
 */
import React, { useState, useEffect } from "react";
import { useTranslation } from "react-i18next";
import { ToastContainer, toast } from "react-toastify";
import { useNavigate } from "react-router-dom";
import "react-toastify/dist/ReactToastify.css";

import NavbarLangOnly from "../../components/Home/navbarLang/NavbarLangOnly";
import iraqFlag from "../../assets/iraq-flag.png";
import SignupUI from "./SignupUI";
import { useAuth } from "../../context/auth/AuthContext";

export default function Signup() {
  const { i18n } = useTranslation();
  const isArabic = i18n.language === "ar";
  const navigate = useNavigate();
  const { login: authLogin, isAuthenticated, authRole, isLoading } = useAuth();

  /* 🔄 Auto Redirect if Logged In */
  useEffect(() => {
    if (!isLoading && isAuthenticated()) {
      if (authRole === "superAdmin") navigate("/admin");
      else if (authRole === "tenant") navigate("/admin-store");
      else navigate("/");
    }
  }, [isLoading, isAuthenticated, authRole, navigate]);

  const [form, setForm] = useState({
    fullName: "",
    engFullName: "",
    phone: "",
    email: "",
    password: "",
    confirmPassword: "",
    agreeTerms: false,
  });

  const [errors, setErrors] = useState({});
  const [passwordStrength, setPasswordStrength] = useState("");
  const [loading, setLoading] = useState(false);
  const [isMobile, setIsMobile] = useState(window.innerWidth < 576);
  const baseUrl = (process.env.REACT_APP_API_BASE_URL || "https://madar-api.madar.iq/api").replace(/\/+$/, "");

  useEffect(() => {
    const handleResize = () => setIsMobile(window.innerWidth < 576);
    window.addEventListener("resize", handleResize);
    return () => window.removeEventListener("resize", handleResize);
  }, []);

  const COLORS = {
    darkPurple: "#21003D",
    purple: "#7400BF",
    gold: "#F2B441",
    gradient: "linear-gradient(135deg, #21003D, #7400BF)",
  };

  const notify = (type, msg) =>
    toast[type](msg, {
      position: "top-center",
      rtl: isArabic,
      theme: "colored",
      autoClose: 2500,
    });

  /* 🔐 قوة كلمة السر */
  useEffect(() => {
    const val = form.password;
    if (!val) return setPasswordStrength("");

    const strong = /^(?=.*[a-z])(?=.*[A-Z])(?=.*\d)(?=.*[@$!%*#?&]).{8,}$/;
    const medium = /^(?=.*[A-Za-z])(?=.*\d).{6,}$/;

    if (strong.test(val)) setPasswordStrength(isArabic ? "قوية" : "Strong");
    else if (medium.test(val)) setPasswordStrength(isArabic ? "متوسطة" : "Medium");
    else setPasswordStrength(isArabic ? "ضعيفة" : "Weak");
  }, [form.password, isArabic]);

  const getStrengthColor = () =>
    passwordStrength.match(/Strong|قوية/) ? "#16a34a" :
      passwordStrength.match(/Medium|متوسطة/) ? "#f97316" :
        passwordStrength ? "#dc2626" : "#d1d5db";

  const getStrengthValue = () =>
    passwordStrength.match(/Strong|قوية/) ? "100%" :
      passwordStrength.match(/Medium|متوسطة/) ? "60%" :
        passwordStrength ? "30%" : "0%";

  /* ✅ التحقق من الحقول */
  const validate = () => {
    const e = {};
    const phoneR = /^7\d{9}$/; // رقم يبدأ بـ 7
    const mailR = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    const passR = /^(?=.*[a-z])(?=.*[A-Z])(?=.*\d)(?=.*[^A-Za-z0-9]).{8,}$/;

    if (!form.fullName.trim())
      e.fullName = isArabic ? "يرجى إدخال الاسم بالعربية" : "Arabic name required";

    if (!form.engFullName.trim())
      e.engFullName = isArabic ? "يرجى إدخال الاسم بالإنجليزية" : "English name required";

    if (!phoneR.test(form.phone))
      e.phone = isArabic ? "رقم الهاتف غير صحيح" : "Invalid phone";

    if (!mailR.test(form.email))
      e.email = isArabic ? "بريد غير صحيح" : "Invalid email";

    if (!passR.test(form.password))
      e.password = isArabic ? "كلمة السر ضعيفة" : "Weak password";

    if (form.password !== form.confirmPassword)
      e.confirmPassword = isArabic ? "كلمتا السر غير متطابقتين" : "Passwords do not match";

    if (!form.agreeTerms)
      e.agreeTerms = isArabic ? "يجب الموافقة على سياسة الخصوصية وشروط الاستخدام" : "You must agree to the Privacy Policy and Terms of Use";

    setErrors(e);
    return Object.keys(e).length === 0;
  };

  const handleChange = (e) => {
    const { name, value, type, checked } = e.target;
    setForm({ ...form, [name]: type === "checkbox" ? checked : value });
  };

  /* =====================================================
     🚀 النسخة النهائية الصحيحة من handleSubmit
     ===================================================== */
  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!validate()) {
      notify("error", isArabic ? "تحقق من الحقول" : "Fix the form");
      return;
    }

    setLoading(true);

    try {
      const body = {
        tenantName: form.fullName,
        engTenantName: form.engFullName,
        phone: form.phone,
        email: form.email,
        password: form.password,
        confirmPassword: form.confirmPassword,
      };

      const res = await fetch(`${baseUrl}/Register/register`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Accept: "application/json",
        },
        body: JSON.stringify(body),
      });

      const contentType = res.headers.get("content-type") || "";
      const data = contentType.includes("application/json") ? await res.json() : await res.text();
      setLoading(false);

      if (!res.ok) {
        const message = typeof data === "string" ? data : data?.message || `HTTP ${res.status}`;
        notify("error", message);
        return;
      }

      /* =====================================================
         🌟 SAVE FULL RESPONSE TO LOCALSTORAGE
         ===================================================== */
      localStorage.setItem("fullRegisterResponse", JSON.stringify(data));
      localStorage.setItem("token", data?.token || "");

      localStorage.setItem("tenant", JSON.stringify(data?.tenant || {}));
      localStorage.setItem("tenantId", data?.tenant?.tenantId || "");
      localStorage.setItem("tenantEmail", data?.tenant?.email || "");
      localStorage.setItem("tenantDB", data?.tenant?.databaseName || "");

      localStorage.setItem("tenantUsersList", JSON.stringify(data?.users || []));

      // حفظ صلاحيات الـ Owner
      if (data?.users?.length > 0) {
        localStorage.setItem("permissions", JSON.stringify(data.users[0]));
      }

      // حفظ الباقات إذا رجعت
      if (data?.plans) {
        localStorage.setItem("plans", JSON.stringify(data.plans));
      }

      // حفظ الثيمات إذا رجعت
      if (data?.themes) {
        localStorage.setItem("themes", JSON.stringify(data.themes));
      }

      // حفظ طرق الدفع إذا رجعت
      if (data?.paymentMethods) {
        localStorage.setItem("paymentMethods", JSON.stringify(data.paymentMethods));
      }

      /* =====================================================
         🔐 تسجيل المستخدم تلقائيًا بعد نجاح التسجيل
         ===================================================== */
      authLogin({
        token: data.token,
        user: data.users[0],
        authRole: "tenant",
        isTenantOwner: true,
        permissions: data.users[0], // المالك لديه كل الصلاحيات
      });

      notify(
        "success",
        isArabic ? "تم إنشاء الحساب بنجاح" : "Account created successfully"
      );

      setTimeout(() => navigate("/company-info"), 1200);
    } catch (error) {
      setLoading(false);
      notify("error", "Network error");
    }
  };

  return (
    <>
      <NavbarLangOnly />
      <ToastContainer />

      <SignupUI
        COLORS={COLORS}
        isArabic={isArabic}
        isMobile={isMobile}
        form={form}
        errors={errors}
        passwordStrength={passwordStrength}
        getStrengthColor={getStrengthColor}
        getStrengthValue={getStrengthValue}
        loading={loading}
        iraqFlag={iraqFlag}
        handleChange={handleChange}
        handleSubmit={handleSubmit}
      />
    </>
  );
}
